export type JawType = "upper" | "lower";
export type ViewMode = "upper" | "lower" | "both";

export type ProcedureType =
  | "crown"
  | "implant"
  | "bridge"
  | "veneer"
  | "inlay_onlay"
  | "custom_abutment";

export type PrescriptionStatus =
  | "prescribed"
  | "in_planning"
  | "ready_for_production"
  | "sent_to_production"
  | "completed";

export type RestorationPlanStatus =
  | "not_started"
  | "in_design"
  | "ready_for_production"
  | "approved"
  | "sent_to_production";

export type TargetType =
  | "single_tooth"
  | "multiple_teeth"
  | "range";


  export type ViewPreset =
  | "front"
  | "back"
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "reset";

export type ViewportCommandType =
  | "fit"
  | "zoomIn"
  | "zoomOut"
  | "view";

export interface ViewportCommand {
  type: ViewportCommandType;
  preset?: ViewPreset;
  nonce: number;
}

export type Vec3 = [number, number, number];

export const DEFAULT_TOOTH_PLACEMENT_ADJUSTMENT: ToothPlacementAdjustment = {
  rotationDeg: [0, 0, 0],
  translation: [0, 0, 0],
};


export interface DentalCaseSummary {
  caseId: string;
  caseName: string;
  patientId: string;
  patientName: string;
  doctorName: string;
}

export interface ArchDefinition {
  jawType: JawType;
  width: number;
  depth: number;
  height: number;
  toothCount: number;
}

export interface DentalCaseJaws {
  upper: ArchDefinition;
  lower: ArchDefinition;
}

export interface PrescriptionTarget {
  targetType: TargetType;
  jaw: JawType;
  teeth: string[];
  rangeStart?: string;
  rangeEnd?: string;
}

export interface PrescriptionItem {
  itemId: string;
  title: string;
  procedureType: ProcedureType;
  status: PrescriptionStatus;
  target: PrescriptionTarget;
  doctorNotes: string;
  material?: string;
  shade?: string;
  productionNotes?: string;
}

export interface RestorationPlan {
  planId: string;
  prescriptionItemId: string;
  title: string;
  procedureType: ProcedureType;
  status: RestorationPlanStatus;
  targetJaw: JawType;
  targetTeeth: string[];
  selectedCatalogItem?: string;
  material?: string;
  shade?: string;
  spacingNotes?: string;
  designNotes?: string;
  productionNotes?: string;
}

export interface DentalCaseData {
  summary: DentalCaseSummary;
  jaws: DentalCaseJaws;
  prescriptionItems: PrescriptionItem[];
  restorationPlans: RestorationPlan[];
}

export interface PatientListItem {
  patientId: string;
  patientName: string;
  caseId: string;
  caseName: string;
}

/** ---------- Tooth definition / placement models ---------- */

export interface ToothDefinition {
  toothId: string;
  jawType: JawType;

  /**
   * Future-facing static metadata about the asset itself.
   * These are intrinsic to the tooth definition / library entry.
   */
  modelAssetKey?: string;
  insertionPointLocal?: Vec3;
  crownCenterLocal?: Vec3;
  rootCenterLocal?: Vec3;
  defaultRotationDeg?: Vec3;
  nominalSize?: Vec3;
}

export interface ComputedToothPlacement {
  toothId: string;
  jawType: JawType;

  /**
   * Placement data derived from the current arch / scene.
   */
  archPoint: Vec3;
  archTangent: Vec3;
  basePosition: Vec3;
  baseRotationDeg: Vec3;
}

export interface ToothPlacementAdjustment {
  rotationDeg: Vec3;
  translation: Vec3;
}

export type ToothAdjustmentMap = Record<string, ToothPlacementAdjustment>;

/** ---------- UI / viewport models for workbench ---------- */

export interface ToothModel {
  id: string;
  toothNumber: string;
  jaw: JawType;
  x: number;
  y: number;
  width: number;
  height: number;
  label?: string;
  isMissing?: boolean;
  isDisabled?: boolean;
}

export interface WorkbenchSelectionState {
  selectedToothId: string | null;
  hoveredToothId: string | null;
}

export interface WorkbenchUiState {
  viewMode: ViewMode;
  activeProcedure: ProcedureType | null;
  isDebugOpen: boolean;
  selection: WorkbenchSelectionState;
}


